import { CompanyContext } from "contexts/CompanyContext";
import { SessionContext } from "contexts/SessionContext"
import { AIContext } from "contexts/AIContext";
import { useContext } from "react"
import useCreativeGenerator from "./useCreativeGenerator";
import useCopyGenerator, { contextToText } from "./useCopyGenerator";
import usePostValidator, { getNetworkValidationIndex } from "./usePostValidator";
import { usePinterest } from "./socials/usePinterest";
import { stripHTMLReplaceWithNewLines } from "utils/stripHTML";
import { postTypesOptions } from "./useConfigPanel";
import { PUBLIC_URL } from "components/ui/utils/constant";
import { socialNetworksMediaProductTypes } from "views/calendar/SocialNetworkSidePanel";
import { getNetworkCharCountUnformatted } from "components/layout/MagicEditor/OptionsBar";

export const usePostGenerator = () => {

  const { user } = useContext(SessionContext);
  const { company } = useContext(CompanyContext);
  const { generateStockImageFromCopy, generateStockGIFFromCopy, generateAIImageFromCopy, generateAIVideoFromCopy } = useCreativeGenerator();
  const { generateCopies, generateTitleFromContext, generateMultipleCopiesFromCopy } = useCopyGenerator();
  const { ensureOnboarded } = useContext(AIContext);
  const { validationConstants } = usePostValidator();
  const { getPinterestBoards } = usePinterest();

  const _formatPost = (network, copy = "", assets = [], title = "", board_id = undefined, media_product_type = undefined, cover = undefined, video_cover_timestamp_ms = undefined) => {
    return { 
      id: undefined,
      posts: [{
        id: undefined,
        user_id: user?.id,
        social_network_type: network,
        description: copy,
        assets: assets,
        title: title,
        link: company?.website,
        cover: cover,
        preferences: { 
          media_product_type: media_product_type || "FEED",
          board_id: board_id,
          video_cover_timestamp_ms: video_cover_timestamp_ms
        },
      }],
      publish_at: undefined,
      draft: 0,
      auto_publish: 1,
      user: user,
      randomId: Math.random(),
      created_at: new Date()
    };
  }

  const getAssetsForNetwork = (networkValidationIndex, assets) => {
    const maxAssets = validationConstants?.[networkValidationIndex]?.media_urls?.max_length || 0;
    const maxImages = validationConstants?.[networkValidationIndex]?.media_urls?.max_images_length || 0;
    const maxVideos = validationConstants?.[networkValidationIndex]?.media_urls?.max_videos_length || 0;
    const allowMultipleTypes = validationConstants?.[networkValidationIndex]?.media_urls?.allow_multiple_types !== false;
    
    let images = assets?.filter(a => !!a?.image?.url);
    let videos = assets?.filter(a => !!a?.video?.url);

    if (!allowMultipleTypes && assets?.length > 0) {
      const firstAssetType = assets[0]?.video?.url ? 'video' : 'image';
      if (firstAssetType === 'video') {
        images = [];
      } else {
        videos = [];
      }
    }

    if (images?.length > maxImages) images.length = maxImages;
    if (videos?.length > maxVideos) videos.length = maxVideos;
    
    assets = [...images, ...videos];
    if (assets?.length > maxAssets) assets.length = maxAssets;

    return assets;
  }

  const generateEmptyPost = (network, media_product_type = undefined) => {
    return _formatPost(
      network, 
      undefined, 
      undefined, 
      undefined, 
      undefined, 
      media_product_type
    );
  }

  const copyFromOtherPost = (network, copy, assets = [], title = "", media_product_type = "FEED", cover = undefined, video_cover_timestamp_ms = undefined) => {
    if (!socialNetworksMediaProductTypes[network]?.includes(media_product_type)) {
      media_product_type = socialNetworksMediaProductTypes[network]?.[0];
    }
    copy = network !== "medium" ? stripHTMLReplaceWithNewLines(copy) : copy?.replaceAll(`\n`,`<br/>`);
    assets = getAssetsForNetwork(getNetworkValidationIndex(network, media_product_type), assets);
    return _formatPost(
      network, 
      copy, 
      assets, 
      validationConstants?.[getNetworkValidationIndex(network, media_product_type)]?.title?.required ? title : "",
      undefined,
      media_product_type,
      cover,
      video_cover_timestamp_ms
    );
  }
  
  const generateFromOtherPost = async (network, copy, assets = [], media_product_type = "FEED") => {
    if (!socialNetworksMediaProductTypes[network]?.includes(media_product_type)) {
      media_product_type = socialNetworksMediaProductTypes[network]?.[0];
    }
    const maxLength = validationConstants?.[getNetworkValidationIndex(network, media_product_type)]?.description?.max_length;
    // Stories take no text (max_length 0), so there is nothing to write.
    const [generated] = copy && maxLength !== 0
      ? await generateCopies(network, { source: { kind: "post", content: copy } }, {
          imageURLs: assets?.filter(e => !!e?.image?.url)?.map(e => e?.image?.url),
          quantity: 1,
          maxLength,
          mediaProductType: media_product_type
        })
      : [];
    copy = generated?.text || "";
    assets = getAssetsForNetwork(getNetworkValidationIndex(network, media_product_type), assets);
    const title = validationConstants?.[getNetworkValidationIndex(network, media_product_type)]?.title?.required 
      ? generated?.title || (copy ? await generateTitleFromContext(network, copy) : "")
      : "";
    const board_id = validationConstants?.[getNetworkValidationIndex(network, media_product_type)]?.board_id?.required 
      ? (await getPinterestBoards())?.[0]?.id 
      : undefined;
    return _formatPost(
      network, 
      copy, 
      assets, 
      title, 
      board_id,
      media_product_type
    );
  }
  
  const generateMultiplePostsFromPost = async (post) => {
    // Every part has to fit the network's text limit: nothing checks it again before the queue
    // publishes it (Threads rejects a part over 500 characters and the rest of the thread fails).
    const network = post?.social_network_type;
    const maxLength = validationConstants?.[getNetworkValidationIndex(network, post?.preferences?.media_product_type)]?.description?.max_length;
    const copies = await generateMultipleCopiesFromCopy(post?.description, maxLength, (text) => getNetworkCharCountUnformatted(network, text), network);
    const posts = copies.map((copy,i) => _formatPost(
      post.social_network_type, 
      copy, 
      i == 0 ? post?.assets : undefined,
      post?.title, 
      post?.preferences?.board_id
    )?.posts?.[0]);
    return posts;
  }
  
  /**
   * Generates `quantity` post groups from one context (a string brief, or `{ brief, source, link }`).
   * `instructions` is `{ tone, format, cta, extra_instructions }` (a string is taken as the extra
   * instructions). `offset` and `avoid` keep "generate more" from repeating earlier copies, and
   * every post group is tagged with `contextIndex`.
   */
  const generateFromContext = async (network, context, instructions = {}, images = [], imageType = "ai", media_product_type = undefined, prompts = [], quantity = 1, { offset = 0, avoid = [], contextIndex } = {}) => {
    if (!socialNetworksMediaProductTypes[network]?.includes(media_product_type)) {
      media_product_type = socialNetworksMediaProductTypes[network]?.[0];
    }
    if (typeof instructions === "string") instructions = { extra_instructions: instructions };
    if (!instructions || typeof instructions !== "object") instructions = {};
    quantity = Math.max(1, parseInt(quantity) || 1);
    offset = Math.max(0, parseInt(offset) || 0);

    const networkValidationIndex = getNetworkValidationIndex(network, media_product_type);
    const constants = validationConstants?.[networkValidationIndex];
    const maxLength = constants?.description?.max_length;
    const contextText = contextToText(context);
    // Calculate postType
    const postType = postTypesOptions.find(e => e.value == network);

    const generateImage = async (text) => {
      return imageType === "source" && !!images?.length
        ? images?.[0]
        : imageType === "stock"
          ? await generateStockImageFromCopy(network, text)
          : imageType === "gif"
            ? await generateStockGIFFromCopy(network, text)
            : imageType === "ai"
              ? await generateAIImageFromCopy(network, text, media_product_type)
              : undefined;
    }

    const buildPostGroup = async ({ text: copy = "", title: copyTitle = "" } = {}) => {
      // Create image
      const image = constants?.media_urls?.max_images_length
        ? await generateImage(copy || contextText)
        : undefined;
      // Create video
      const video = constants?.media_urls?.max_videos_length
        ? imageType === "ai_video"
          ? await generateAIVideoFromCopy(network, copy || contextText, media_product_type)
          : undefined
        : undefined;
      // Title: the copy request writes it for networks that need one; ask for it only if missing
      const title = constants?.title?.required
        ? copyTitle || (copy ? await generateTitleFromContext(network, copy) : "")
        : "";
      // Calculate board ID
      const board_id = constants?.board_id?.required ? (await getPinterestBoards())?.[0]?.id : undefined;
      // Create post group
      let postGroup = _formatPost(
        network,
        copy,
        image
          ? [{
            image: {
              url: image?.url || image
            }
          }]
          : video
            ? [{
              video: {
                url: video?.url || video
              }
            }]
          : undefined,
        title,
        board_id,
        media_product_type
      );

      // Split into multiple posts if required
      if (postType?.allowMultiplePosts && copy?.length > postType?.makeMultiplePostsWhenTextLengthLongerThan) {
        postGroup.posts = await generateMultiplePostsFromPost(postGroup.posts?.[0]);
      }
      postGroup.contextIndex = contextIndex;
      return postGroup;
    }

    const settledGroups = async (promises) => {
      const results = await Promise.allSettled(promises);
      return results.filter(r => r.status === "fulfilled" && !!r.value).map(r => r.value);
    }

    // Stories take no text (max_length 0): skip the copy request
    if (maxLength === 0) {
      return settledGroups(Array.from({ length: quantity }, () => buildPostGroup({ text: "", title: "" })));
    }

    // Medium places its image inside the article, so the image comes first and each variation
    // (one per request anyway) gets its own image.
    if (network === "medium") {
      if (!ensureOnboarded()) return [];
      return settledGroups(Array.from({ length: quantity }, async (_, i) => {
        const imageURL = await generateImage(contextText);
        const [copy] = await generateCopies(network, context, {
          instructions,
          prompts,
          imageURLs: imageURL ? [imageURL] : [],
          quantity: 1,
          offset: offset + i,
          avoid,
          maxLength,
          mediaProductType: media_product_type
        });
        return copy ? buildPostGroup(copy) : undefined;
      }));
    }

    // Create copies (one batch for every variation)
    const copies = await generateCopies(network, context, {
      instructions,
      prompts,
      quantity,
      offset,
      avoid,
      maxLength,
      mediaProductType: media_product_type
    });
    return settledGroups(copies.map((copy) => buildPostGroup(copy)));
  }

  const generatePostGroupFromSocialPost = (network, post, socialNetworkData) => {
    let formattedPost;

    if (network === "facebook") {
      const media = post?.attachments?.data?.[0]?.subattachments?.data?.length > 0 
        ? post?.attachments?.data?.[0]?.subattachments?.data?.map(item => ({
            type: item?.type == "photo" ? "image" : item?.type?.includes("video") ? "video" : item?.type,
            url: item?.media?.source ? item?.media?.source : item?.media?.image?.src,
            width: item?.media?.image?.width,
            height: item?.media?.image?.height
          }))
        : [{
            type: post?.attachments?.data?.[0]?.type == "photo" || "profile_media" ? "image" : post?.attachments?.data?.[0]?.media?.type?.includes("video") ? "video" : (post?.attachments?.data?.[0]?.media?.type || post?.attachments?.data?.[0]?.type),
            url: post?.attachments?.data?.[0]?.media?.source ? post?.attachments?.data?.[0]?.media?.source : post?.attachments?.data?.[0]?.media?.image?.src,
            width: post?.attachments?.data?.[0]?.media?.image?.width,
            height: post?.attachments?.data?.[0]?.media?.image?.height
          }];

      formattedPost = {
        id: post?.id,
        permalink: post?.permalink_url || post?.permalink,
        social_network_type: "facebook",
        description: post?.message,
        assets: media.map(item => ({
          image: item.type === "image" ? { url: item.url } : undefined,
          video: item.type === "video" ? { url: item.url } : undefined
        })),
        title: "",
        link: post?.permalink_url,
        preferences: { media_product_type: "FEED" },
        insights: {
          likes: 
            post?.insights?.data?.find(e => e.name == 'likes')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_activity_by_action_type')?.values?.[0]?.value?.like || 
            0,
          comments: 
            post?.insights?.data?.find(e => e.name == 'comments')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_activity_by_action_type')?.values?.[0]?.value?.comment || 
            0,
          shares: 
            post?.insights?.data?.find(e => e.name == 'shares')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_activity_by_action_type')?.values?.[0]?.value?.share || 
            0,
          views: 
            post?.insights?.data?.find(e => e.name == 'impressions')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_impressions')?.values?.[0]?.value || 
            0,
          follows: 
            post?.followers_count || 
            0,
        }
      };
    } else if (network === "instagram") {
      const allAssets = post?.media_type === "CAROUSEL_ALBUM" 
        ? post?.children?.data?.map(child => ({
            type: child?.media_type?.toLowerCase(),
            url: child?.media_url,
            width: child?.width,
            height: child?.height,
            thumbnail: {
              url: child?.thumbnail_url || child?.media_url,
              type: 'image'
            },
          }))
        : [{
            type: post?.media_type?.toLowerCase(),
            url: post?.media_url,
            width: post?.width,
            height: post?.height,
            thumbnail: {
              url: post?.thumbnail_url || post?.media_url,
              type: 'image'
            },
          }];

      formattedPost = {
        id: post?.id,
        permalink: post?.permalink_url || post?.permalink,
        social_network_type: "instagram",
        description: post?.caption,
        assets: allAssets.map(item => ({
          image: item.type === "image" 
            ? { 
                url: item.url,
                width: item.width,
                height: item.height,
                thumbnail: item.thumbnail
              } 
            : undefined,
          video: item.type === "video" 
            ? { 
                url: item.url,
                width: item.width,
                height: item.height,
                thumbnail: item.thumbnail
              } 
            : undefined
        })),
        title: "",
        link: post?.permalink,
        preferences: { media_product_type: "FEED" },
        insights: {
          likes: 
            post?.insights?.data?.find(e => e.name == 'likes')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_activity_by_action_type')?.values?.[0]?.value?.like || 
            0,
          comments: 
            post?.insights?.data?.find(e => e.name == 'comments')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_activity_by_action_type')?.values?.[0]?.value?.comment || 
            0,
          shares: 
            post?.insights?.data?.find(e => e.name == 'shares')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_activity_by_action_type')?.values?.[0]?.value?.share || 
            0,
          views: 
            post?.insights?.data?.find(e => e.name == 'impressions')?.values?.[0]?.value || 
            post?.insights?.data?.find(e => e.name == 'post_impressions')?.values?.[0]?.value || 
            0,
          follows: 
            post?.followers_count || 
            0,
        }
      };
    }
    
    formattedPost = {
      ..._formatPost(
        formattedPost.social_network_type,
        formattedPost.description,
        formattedPost.assets,
        formattedPost.title,
        undefined,
        formattedPost.preferences.media_product_type
      ),
      ...formattedPost
    };

    formattedPost.posts[0].user_image = socialNetworkData?.image?.url || `${PUBLIC_URL}/img/others/auth-cover-bg.jpg`;
    formattedPost.posts[0].user_name = socialNetworkData?.name || socialNetworkData?.username || "Unknown";

    return formattedPost;
  };

  const generatePostWithNewMediaProductType = (post, network, newMediaProductType) => {
    const networkValidationIndex = getNetworkValidationIndex(network, newMediaProductType);
    let newPost = generateEmptyPost(network, newMediaProductType);
    
    newPost = {
      ...newPost?.posts?.[0],
      description: post?.description || post?.text,
      assets: getAssetsForNetwork(networkValidationIndex, post?.assets),
      title: post?.title,
      link: post?.link,
      comments_to_create: post?.comments_to_create,
      preferences: {
        ...post?.preferences,
        media_product_type: newMediaProductType
      }
    };

    // Adjust description based on validation constants
    const constants = validationConstants?.[networkValidationIndex];
    if (constants?.description?.max_length !== undefined) {
      newPost.description = newPost.description?.substring(0, Math.min(constants.description.max_length, newPost.description?.length));
    }

    return newPost;
  };

  return { 
    generateFromContext, 
    generateFromOtherPost, 
    generateEmptyPost, 
    copyFromOtherPost, 
    generatePostGroupFromSocialPost,
    getAssetsForNetwork,
    generatePostWithNewMediaProductType
  }
}

export default usePostGenerator;